import React, { useRef } from 'react';
import { capitalize } from 'lodash';
import classNames from 'classnames';

import { stepTypesLabels } from '../../../constants/compound';
import { formatClosingRange, upToNowValue } from '../../../utils/compound';
import ExpandableText from '../../shared/ExpandableText';
import StepFilterDetails from '../../../containers/filter/StepFilterDetails';
import CompoundExecutionStepProgressDetails from './StepProgressDetails';
import ClosingRange from '../../funnel/execution/ClosingRange';

import useContentOverflow from '../../../utils/hooks/useContentOverflow';

const getStepTypeLabel = (stepType, stepNum) =>
  stepNum === 1 ? stepTypesLabels.FIRST : capitalize(stepTypesLabels[stepType]);

const renderClosingRange = (closingRange, fromDashboard) => {
  if (closingRange.value === 0) {
    return <span>Closing range: Not specified</span>;
  }

  if (closingRange.value === upToNowValue) {
    return <span>Closing range: Up to now</span>;
  }

  return (
    <span>
      <ClosingRange {...formatClosingRange(closingRange)} fromDashboard={fromDashboard} />
    </span>
  );
};

const CompoundExecutionStepDetails = ({
  totalCount,
  stepNum,
  prevStepCount,
  stepCount,
  stepEvent,
  stepType,
  stepFilter,
  stepClosingRange,
  disabled,
  optional,
  prevStepOptionalCount,
  optionalStepCount,
  stepRequiredCount,
  stepRequiredCountOperator,
  stepDescription,
  fromDashboard,
  stepLegendName,
}) => {
  const titleRef = useRef(null);
  const isOverflow = useContentOverflow(titleRef);
  const titleTooltip = fromDashboard && isOverflow ? stepLegendName || stepEvent : '';

  return disabled ? (
    <div
      style={{
        padding: '1.2em',
        background: '#D8D8D8',
        borderStyle: 'solid',
        borderColor: '#999',
        borderTopWidth: stepNum - 1 ? '0px' : '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
      }}
    >
      <h3 style={{ margin: 0, color: '#999999' }}>{stepEvent}</h3>
    </div>
  ) : (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '1.2em',
        borderStyle: 'solid',
        borderColor: '#999',
        borderTopWidth: stepNum - 1 ? '0px' : '1px',
        borderRightWidth: '1px',
        borderBottomWidth: '1px',
        borderLeftWidth: '1px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexShrink: 0,
          width: fromDashboard ? '250px' : '500px',
        }}
      >
        <div>
          <h3
            className={classNames(optional && 'funnel-optional-text')}
            style={{
              margin: 0,
              ...(fromDashboard && {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }),
            }}
            title={titleTooltip}
            ref={titleRef}
          >
            {`${stepNum}.`}&nbsp;
            {!!stepType && (
              <span style={{ fontWeight: 'lighter' }}>
                {getStepTypeLabel(stepType, stepNum)}&nbsp;
              </span>
            )}
            {stepLegendName || stepEvent}
          </h3>
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', margin: '10px 0px', width: '100%' }}
        >
          <span>
            Events count (per user): {stepRequiredCountOperator} {stepRequiredCount}
          </span>
          {stepClosingRange && renderClosingRange(stepClosingRange, fromDashboard)}
          {stepDescription && (
            <div style={{ display: 'flex' }}>
              <span style={{ marginRight: 10 }}>Description:</span>
              <ExpandableText text={stepDescription} limit={55} />
            </div>
          )}
        </div>
        <div>
          {stepFilter && stepFilter.clauses.length && !fromDashboard ? (
            <StepFilterDetails filter={stepFilter} event={stepEvent} />
          ) : null}
        </div>
      </div>
      {(stepCount != null || optionalStepCount != null) && (
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, marginLeft: 50 }}>
          <CompoundExecutionStepProgressDetails
            stepCount={optional ? optionalStepCount : stepCount}
            prevStepCount={prevStepCount}
            totalCount={totalCount}
            optional={optional}
            fromDashboard={fromDashboard}
          />
          {!optional && optionalStepCount != null ? (
            <CompoundExecutionStepProgressDetails
              stepCount={optionalStepCount}
              prevStepCount={prevStepOptionalCount}
              totalCount={totalCount}
              optional
              style={{ marginTop: 5 }}
              fromDashboard={fromDashboard}
            />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default CompoundExecutionStepDetails;
